import { useAuthorizedFetch } from "./AuthorizedFetch";
import { enviroment } from "../../enviroment";

export type PlanType = "FreeTierCap" | "FlatPerCall" | "MonthlyWithOverage";
export type BillingPeriod = "Weekly" | "Monthly" | "Quarterly" | "Yearly";
export type Currency = "DKK" | "EUR" | "USD" | "GBP";
export type KeyStatus = "Active" | "Blocked" | "Expired";

export interface ScopeOverride {
  scope: string;
  overageRate: number;
}

export interface PricingPlanDto {
  id: number;
  apiKeyId: number;
  planType: PlanType;
  billingPeriod: BillingPeriod | null;
  baseFee: number | null;
  includedCalls: number | null;
  overageRate: number | null;
  currency: Currency;
  activatedAt: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  scopeOverrides: ScopeOverride[];
}

export interface ApiKeyListDto {
  id: number;
  organizationId: number | null;
  name: string;
  keyIdPrefix: string;
  status: KeyStatus;
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  scopes: string[];
  plan: PricingPlanDto | null;
}

export interface CreateKeyInput {
  organizationId: number;
  name: string;
  scopes: string[];
  plan?: PlanInput;
}

export interface PlanInput {
  planType: PlanType;
  billingPeriod?: BillingPeriod;
  baseFee?: number;
  includedCalls?: number;
  overageRate?: number;
  currency: Currency;
  activatedAt?: string;
  notes?: string;
  scopeOverrides?: ScopeOverride[];
}

export interface CreateKeyResponse {
  id: number;
  token: string;
  organizationId: number;
  name: string;
  scopes: string[];
  expiresAt: string | null;
  plan: PricingPlanDto | null;
}

export interface UsageBucket {
  bucket: string;
  calls: number;
}

export interface StatusCodeBucket {
  statusCode: number;
  calls: number;
}

export interface RouteBucket {
  method: string;
  route: string;
  calls: number;
}

export interface CostBreakdownDto {
  baseFee: number;
  overage: number;
  total: number;
  callsTotal: number;
  callsIncluded: number;
  callsOverage: number;
  currency: string;
  byScope: ScopeCostDto[];
}

export interface ScopeCostDto {
  scope: string;
  calls: number;
  overageCalls: number;
  rate: number;
  cost: number;
}

export interface UsageResponse {
  apiKeyId: number;
  from: string;
  to: string;
  groupBy: string;
  totalCalls: number;
  series: UsageBucket[];
  statusCodes: StatusCodeBucket[];
  topRoutes: RouteBucket[];
  cost: CostBreakdownDto | null;
}

export interface BillingPeriodDto {
  periodStart: string;
  periodEnd: string | null;
  callsTotal: number;
  callsIncluded: number;
  callsOverage: number;
  baseFee: number;
  overage: number;
  total: number;
  currency: string;
  status: string;
  isCurrent: boolean;
}

export interface BillingPeriodsResponse {
  apiKeyId: number;
  periods: BillingPeriodDto[];
}

export interface DashboardSummaryDto {
  totalKeys: number;
  activeKeys: number;
  blockedKeys: number;
  expiredKeys: number;
  mtdCalls: number;
  monthStart: string;
  revenueByCurrency: { currency: string; total: number }[];
  topVendorsThisMonth: {
    apiKeyId: number;
    vendorId: number | null;
    keyName: string | null;
    calls: number;
  }[];
}

export interface KnownScopeDto {
  scope: string;
  routes: { method: string; route: string; functionName: string }[];
}

export interface ExternalApiClient {
  listKeys(): Promise<ApiKeyListDto[]>;
  createKey(input: CreateKeyInput): Promise<CreateKeyResponse>;
  updateKeyStatus(id: number, status: KeyStatus): Promise<void>;
  updateKeyScopes(id: number, scopes: string[]): Promise<void>;
  getPlan(id: number): Promise<PricingPlanDto | null>;
  upsertPlan(id: number, input: PlanInput): Promise<PricingPlanDto>;
  getUsage(
    id: number,
    from: string,
    to: string,
    groupBy: "day" | "hour" | "scope",
  ): Promise<UsageResponse>;
  getBillingPeriods(id: number, limit: number): Promise<BillingPeriodsResponse>;
  getDashboardSummary(): Promise<DashboardSummaryDto>;
  listScopes(): Promise<KnownScopeDto[]>;
}

export function useExternalApiClient(): ExternalApiClient {
  // Admin API-keys calls now go through the server-side proxy at /api/tools/*.
  // authFetch injects the MSAL bearer token; the server holds the admin key and
  // stamps X-Admin-Identity from the authenticated user, so the browser never sees
  // the key nor supplies the identity.
  const { authFetch } = useAuthorizedFetch();
  const baseUrl = enviroment.apiUrl;

  async function call<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    // authFetch merges this init over its own { headers: { Authorization } }. An
    // explicit `headers` object here would REPLACE that header and break auth, so
    // pass ONLY { method, body }.
    const response = await authFetch(`${baseUrl}${path}`, {
      method,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    // authFetch already throws DataError on non-2xx responses.
    if (response.status === 204) return undefined as unknown as T;
    const text = await response.text();
    if (!text) return undefined as unknown as T;
    return JSON.parse(text) as T;
  }

  return {
    listKeys: () => call<ApiKeyListDto[]>("GET", "/tools/keys"),
    createKey: (input) =>
      call<CreateKeyResponse>("POST", "/tools/generate-key", input),
    updateKeyStatus: async (id, status) => {
      await call<void>("PUT", `/tools/keys/${id}/status`, { status });
    },
    updateKeyScopes: async (id, scopes) => {
      await call<void>("PUT", `/tools/keys/${id}/scopes`, { scopes });
    },
    getPlan: (id) => call<PricingPlanDto | null>("GET", `/tools/keys/${id}/plan`),
    upsertPlan: (id, input) =>
      call<PricingPlanDto>("PUT", `/tools/keys/${id}/plan`, input),
    getUsage: (id, from, to, groupBy) =>
      call<UsageResponse>(
        "GET",
        `/tools/keys/${id}/usage?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&groupBy=${groupBy}`,
      ),
    getBillingPeriods: (id, limit) =>
      call<BillingPeriodsResponse>(
        "GET",
        `/tools/keys/${id}/billing-periods?limit=${limit}`,
      ),
    getDashboardSummary: () =>
      call<DashboardSummaryDto>("GET", "/tools/dashboard/summary"),
    listScopes: () => call<KnownScopeDto[]>("GET", "/tools/scopes"),
  };
}
